import { DBPharmacy } from 'db/schema/pharmacies';
import { DBProviders } from 'db/schema/providers';
import parse from 'html-react-parser';
import { Pharmacy, ProviderLocation } from 'types/SunFire';

// Returns 2 decimal places (e.g. $123.45, $0.00)
export const dollarFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  style: 'currency',
});

// Returns a number rounded to the next whole dollar (e.g. $113.20 => $113, $113.00 => $113, $0.00 => $0)
export const wholeDollarFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 0,
  style: 'currency',
});

export const snakeToCamel = (str: string) => {
  if (!str.includes('_')) return str;
  return str
    .toLowerCase()
    .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('_', ''));
};

export const getNumberInString = (value: string) => {
  const numberInString = value?.replaceAll(',', '')?.match(/\d+(\.\d+)?/);

  if (!numberInString) {
    return value;
  }
  if (numberInString) {
    if (numberInString.input?.includes('-')) {
      return numberInString.input?.split(' ')[0];
    }
    return parseFloat(numberInString[0]);
  }
};

export const convertToMG = (itemConverted: string) => {
  const itemConvertedNum = getNumberInString(itemConverted);

  // If prescriptions are in grams need to convert to mg to get correct sorting
  return itemConverted.toLowerCase().includes(' gm') &&
    typeof itemConvertedNum === 'number'
    ? itemConvertedNum * 1000
    : itemConvertedNum;
};
export const decodeRichTextHtml = (value: string): string =>
  value?.replace(/&lt;/g, '<')?.replace(/&gt;/g, '>') || '';

type KontentElement = {
  type: string;
  value: string | unknown | { description?: string; url?: string }[];
};

function parseAssetElementValue(value: unknown): { alt: string; src: string } {
  const asset = value as { description?: string; url?: string }[];
  return {
    alt: asset[0]?.description || 'alt text',
    src: asset[0]?.url || '',
  };
}

function parseKontentElementValue(element: KontentElement): unknown {
  if (element.type === 'asset') {
    return parseAssetElementValue(element.value);
  }
  if (element.type === 'number') {
    return element.value as number;
  }
  if (element.type === 'rich_text') {
    return parse(decodeRichTextHtml(element.value as string)) || '';
  }
  return element.value || '';
}

export const extractElementValue = (elements: {
  [key: string]: KontentElement;
}) => {
  if (!elements) return {};

  return Object.keys(elements).reduce((acc, key) => {
    if (elements[key] && 'value' in elements[key]) {
      acc[key] = parseKontentElementValue(elements[key]);
    }
    return acc;
  }, {} as Record<string, unknown>);
};

const trimOrEmpty = (value: string | null | undefined) => (value ?? '').trim();

// Returns a formatted address (e.g. 1234 Apple St. Apt. 3D, Chicago, IL, 60642)
export const formatAddress = ({
  address1,
  address2,
  city,
  state,
  zip,
}: Pharmacy | DBPharmacy | ProviderLocation | DBProviders) => {
  const firstLine = [address1, address2].map(trimOrEmpty).join(' ').trim();
  const lastLine = [city, state, zip].map(trimOrEmpty);

  return [firstLine, ...lastLine].filter(Boolean).join(', ');
};

export const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

export const parseCurrencyToInt = (currency: string): number | null => {
  const cleaned = currency.replace(/[^0-9.]/g, '');
  if (!cleaned) {
    return null;
  }

  const value = parseFloat(cleaned);
  if (Number.isNaN(value)) {
    return null;
  }

  return Math.floor(value);
};

// Returns a Medigap plan letter label (e.g. Plan G, Plan F High Deductible)
export const formatGapFilterLabel = (value: string) =>
  value.length === 1 ? `Plan ${value}` : `Plan ${value[0]} High Deductible`;

// Returns a dollar amount ($0, $123, $123.45) or 'N/A' if negative
export const formatCurrency = (amount: number, showDecimals = false) => {
  if (amount < 0) return 'N/A';
  if (amount === 0) return '$0';

  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: showDecimals || amount % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};
